/**
 * Commission Rate Cards editor (GDN-1939).
 *
 * Org-admin surface for configuring how clinicians are paid a percentage of
 * collections. The mere presence of a rate card opts the org into the payroll
 * take-home column/stat-card; clearing all cards opts back out. Gated by the
 * CONFIGURE_COMPENSATION permission.
 *
 * Resolution (backend): per-clinician override > first matching rate group
 * (by license rate class and/or pre-licensed status) > no rate (blank).
 */

import { useEffect, useMemo, useState } from 'react';
import { ProviderPicker } from '@features/appointments';
import { useCan } from '@features/user';
import { Add, Delete, Payments } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { useCompensationConfig, useUpdateCompensationConfig } from '../store/hooks';
import type { ProviderRateClass, RateGroup } from '../types';

const CONFIGURE_COMPENSATION = 'configure_compensation';

const RATE_CLASS_LABELS: Record<ProviderRateClass, string> = {
  PHYSICIAN: 'Physician (MD/DO)',
  PSYCHOLOGIST: 'Psychologist (PhD/PsyD)',
  CLINICAL_SOCIAL_WORKER: 'Clinical social worker',
  COUNSELOR: 'Counselor (LPC/LMHC)',
  MFT: 'Marriage & family therapist',
  NPP: 'Nurse practitioner / NPP',
  RN: 'Registered nurse',
  SUD_COUNSELOR: 'SUD counselor',
  ABA: 'Behavior analyst (ABA)',
  PASTORAL: 'Pastoral counselor',
  PSYCH_ASSOC: 'Psychological associate',
  OTHER: 'Other',
};
const RATE_CLASS_OPTIONS = Object.keys(RATE_CLASS_LABELS) as ProviderRateClass[];

// Local editor row shapes: percent is edited as a human "70" string and
// converted to basis points (7000) only on save, so config carries no floats.
type GroupRow = {
  key: string;
  name: string;
  percent: string;
  preLicensed: 'either' | 'licensed' | 'pre_licensed';
  rateClasses: ProviderRateClass[];
};
type OverrideRow = { id: string; userId: string; percent: string };

const bpsToPercent = (bps: number): string => String(bps / 100);
const percentToBps = (percent: string): number =>
  Math.round(Number(percent || 0) * 100);
const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2)}`;

const preLicensedFromBool = (
  value: boolean | null | undefined
): GroupRow['preLicensed'] =>
  value === true ? 'pre_licensed' : value === false ? 'licensed' : 'either';
const preLicensedToBool = (value: GroupRow['preLicensed']): boolean | null =>
  value === 'pre_licensed' ? true : value === 'licensed' ? false : null;

export function CompensationRateCardsSection() {
  const canConfigure = useCan(CONFIGURE_COMPENSATION);
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading } = useCompensationConfig(undefined, {
    skip: !canConfigure,
  });
  const [save, { isLoading: isSaving }] = useUpdateCompensationConfig();

  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [overrides, setOverrides] = useState<OverrideRow[]>([]);

  useEffect(() => {
    if (!data) return;
    setGroups(
      (data.rate_groups ?? []).map((g: RateGroup) => ({
        key: g.key,
        name: g.name,
        percent: bpsToPercent(g.pct_bps),
        preLicensed: preLicensedFromBool(g.pre_licensed),
        rateClasses: g.rate_classes ?? [],
      }))
    );
    setOverrides(
      (data.overrides ?? []).map(o => ({
        id: newId(),
        userId: o.user_id,
        percent: bpsToPercent(o.pct_bps),
      }))
    );
  }, [data]);

  const isValid = useMemo(
    () =>
      groups.every(g => g.name.trim() && g.percent !== '') &&
      overrides.every(o => o.userId.trim() && o.percent !== ''),
    [groups, overrides]
  );

  if (!canConfigure) return null;

  const handleSave = async () => {
    try {
      await save({
        compensationConfig: {
          rate_groups: groups.map(g => ({
            key: g.key,
            name: g.name.trim(),
            pct_bps: percentToBps(g.percent),
            pre_licensed: preLicensedToBool(g.preLicensed),
            rate_classes: g.rateClasses,
          })),
          overrides: overrides.map(o => ({
            user_id: o.userId.trim(),
            pct_bps: percentToBps(o.percent),
          })),
        },
      }).unwrap();
      enqueueSnackbar('Compensation rate cards saved', { variant: 'success' });
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        'Failed to save rate cards';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const addGroup = () =>
    setGroups(prev => [
      ...prev,
      {
        key: newId(),
        name: '',
        percent: '',
        preLicensed: 'either',
        rateClasses: [],
      },
    ]);
  const updateGroup = (key: string, patch: Partial<GroupRow>) =>
    setGroups(prev => prev.map(g => (g.key === key ? { ...g, ...patch } : g)));
  const removeGroup = (key: string) =>
    setGroups(prev => prev.filter(g => g.key !== key));

  const addOverride = () =>
    setOverrides(prev => [...prev, { id: newId(), userId: '', percent: '' }]);
  const updateOverride = (id: string, patch: Partial<OverrideRow>) =>
    setOverrides(prev => prev.map(o => (o.id === id ? { ...o, ...patch } : o)));
  const removeOverride = (id: string) =>
    setOverrides(prev => prev.filter(o => o.id !== id));

  return (
    <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Payments color="primary" />
          <Typography variant="h6" component="h2">
            Commission Rate Cards
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pay clinicians a percentage of what is collected. Define rate groups by
          license type and supervision status, with per-clinician overrides for
          exceptions. While any rate card exists, the payroll report shows an
          estimated take-home column. Remove all cards to turn this off.
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading rate cards…</Typography>
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Rate groups
            </Typography>
            <Stack spacing={2}>
              {groups.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No rate groups yet.
                </Typography>
              )}
              {groups.map(group => (
                <Card key={group.key} variant="outlined">
                  <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <TextField
                      label="Group name"
                      size="small"
                      value={group.name}
                      onChange={e => updateGroup(group.key, { name: e.target.value })}
                      sx={{ flex: '1 1 160px' }}
                    />
                    <TextField
                      label="Rate"
                      size="small"
                      type="number"
                      value={group.percent}
                      onChange={e => updateGroup(group.key, { percent: e.target.value })}
                      slotProps={{
                        input: {
                          endAdornment: <Typography variant="body2">%</Typography>,
                        },
                      }}
                      sx={{ width: 110 }}
                    />
                    <FormControl size="small" sx={{ width: 180 }}>
                      <InputLabel>Licensure</InputLabel>
                      <Select
                        label="Licensure"
                        value={group.preLicensed}
                        onChange={e =>
                          updateGroup(group.key, {
                            preLicensed: e.target.value,
                          })
                        }
                      >
                        <MenuItem value="either">Any licensure</MenuItem>
                        <MenuItem value="licensed">Fully licensed only</MenuItem>
                        <MenuItem value="pre_licensed">Pre-licensed only</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ flex: '1 1 220px' }}>
                      <InputLabel>License types (any if empty)</InputLabel>
                      <Select
                        multiple
                        label="License types (any if empty)"
                        value={group.rateClasses}
                        onChange={e =>
                          updateGroup(group.key, {
                            rateClasses: e.target.value as ProviderRateClass[],
                          })
                        }
                        input={<OutlinedInput label="License types (any if empty)" />}
                        renderValue={selected => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected).map(value => (
                              <Chip key={value} label={RATE_CLASS_LABELS[value]} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {RATE_CLASS_OPTIONS.map(option => (
                          <MenuItem key={option} value={option}>
                            {RATE_CLASS_LABELS[option]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      aria-label="Remove rate group"
                      onClick={() => removeGroup(group.key)}
                    >
                      <Delete />
                    </IconButton>
                  </CardContent>
                </Card>
              ))}
            </Stack>
            <Button startIcon={<Add />} onClick={addGroup} sx={{ mt: 1 }}>
              Add rate group
            </Button>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Per-clinician overrides
            </Typography>
            <Stack spacing={2}>
              {overrides.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No overrides. Clinicians fall back to the matching rate group.
                </Typography>
              )}
              {overrides.map(override => (
                <Box
                  key={override.id}
                  sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
                >
                  <Box sx={{ flex: '1 1 280px' }}>
                    <ProviderPicker
                      value={override.userId}
                      onChange={userId => updateOverride(override.id, { userId })}
                    />
                  </Box>
                  <TextField
                    label="Rate"
                    size="small"
                    type="number"
                    value={override.percent}
                    onChange={e => updateOverride(override.id, { percent: e.target.value })}
                    slotProps={{
                      input: {
                        endAdornment: <Typography variant="body2">%</Typography>,
                      },
                    }}
                    sx={{ width: 110 }}
                  />
                  <IconButton
                    aria-label="Remove override"
                    onClick={() => removeOverride(override.id)}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
            </Stack>
            <Button startIcon={<Add />} onClick={addOverride} sx={{ mt: 1 }}>
              Add override
            </Button>

            {!isValid && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Every rate group needs a name and rate; every override needs a
                clinician and rate.
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!isValid || isSaving}
                startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
              >
                {isSaving ? 'Saving…' : 'Save rate cards'}
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
